import { ajaxSign } from './sign'

export interface FileAjaxOptions {
	action: string
	data?: FormData
	headers?: object
	withCredentials?: boolean
	// 调整onError参数类型，让错误信息更结构化
	onError: (
		error: {
			type: 'network_error' | 'http_error' | 'parse_error' | 'upload_error'
			message: string
			status: number
			response?: any
			event?: Event
			statusText?: string // 新增：添加statusText可选属性
		},
		status: number
	) => void
	onSuccess: (response: any, status: number) => void
	onProgress: (response: ProgressEvent) => void
}

// 解析响应内容，增加错误捕获的详细信息
function getBody(xhr: XMLHttpRequest): any {
	const text = xhr.responseText || xhr.response
	if (!text) {
		return text
	}

	try {
		return JSON.parse(text)
	} catch (e: any) {
		// 返回解析错误的详细信息，而不只是原始文本
		return {
			rawText: text,
			parseError: {
				message: e.message,
				stack: e.stack
			}
		}
	}
}

export const fileAjax = (option: FileAjaxOptions) => {
	if (typeof XMLHttpRequest === 'undefined') {
		// 触发环境不支持的错误
		option.onError(
			{
				type: 'network_error',
				message: '当前环境不支持 XMLHttpRequest',
				status: 0
			},
			0
		)
		return
	}

	const xhr = new XMLHttpRequest()
	const action = option.action

	// 1. 网络错误处理（断网、连接失败等）
	xhr.onerror = function error(e: Event) {
		option.onError(
			{
				type: 'network_error',
				message: '网络请求失败',
				status: xhr.status,
				event: e
			},
			xhr.status
		)
	}

	// 2. 上传进度回调（保持原有逻辑）
	xhr.upload.addEventListener(
		'progress',
		function (e: ProgressEvent) {
			option.onProgress(e)
		},
		false
	)

	// 3. 上传超时处理（新增，避免请求无响应）
	xhr.ontimeout = function (e: ProgressEvent) {
		option.onError(
			{
				type: 'network_error',
				message: '文件上传请求超时',
				status: 0,
				event: e
			},
			0
		)
	}

	// 4. 加载完成处理（区分成功/失败）
	xhr.onload = function onload() {
		// 成功状态码：200-299
		if (xhr.status >= 200 && xhr.status < 300) {
			const response = getBody(xhr)
			// 检查是否是解析错误
			if (response?.parseError) {
				option.onError(
					{
						type: 'parse_error',
						message: `响应内容解析失败: ${response.parseError.message}`,
						status: xhr.status,
						response: response.rawText
					},
					xhr.status
				)
				return
			}
			option.onSuccess(response, xhr.status)
		} else {
			// HTTP错误（4xx/5xx）：返回完整的错误信息
			const response = getBody(xhr)
			option.onError(
				{
					type: 'http_error',
					message: `HTTP请求失败: ${xhr.status} ${xhr.statusText}`,
					status: xhr.status,
					response: response,
					// 补充返回状态文本，方便调试
					statusText: xhr.statusText
				},
				xhr.status
			)
		}
	}

	// 5. 取消请求的处理（可选，新增）
	xhr.onabort = function (e: Event) {
		option.onError(
			{
				type: 'network_error',
				message: '请求被手动取消',
				status: 0,
				event: e
			},
			0
		)
	}

	xhr.open('post', action, true)

	// 设置是否携带凭证
	if (option.withCredentials && 'withCredentials' in xhr) {
		xhr.withCredentials = true
	}

	// 设置请求头
	const headers = option.headers || {}
	for (const item in headers) {
		if (Object.prototype.hasOwnProperty.call(headers, item) && headers[item] !== null) {
			xhr.setRequestHeader(item, headers[item])
		}
	}

	// 签名处理（保持原有逻辑）
	ajaxSign(action, option.data, xhr, true)

	// 发送请求
	xhr.send(option.data)

	// 返回xhr对象，方便外部取消请求等操作
	return xhr
}
